import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { palette, useThemePalette } from '@/config/theme';
import { effectiveRating } from '@/features/matches/derivedRating';
import type { FixtureWithRefs, PlayerMatchStatWithPlayer } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** FotMob-style rating tint: green (great) → amber (ok) → red (poor). */
export function ratingTone(rating: number): { bg: string; fg: string } {
  if (rating >= 8) return { bg: '#2FA84F', fg: '#FFFFFF' };
  if (rating >= 7) return { bg: palette.volt, fg: palette.onVolt };
  if (rating >= 6.5) return { bg: '#D8A93A', fg: '#1A1408' };
  return { bg: palette.loss, fg: '#FFFFFF' };
}

function displayName(stat: PlayerMatchStatWithPlayer): string {
  const full = stat.player?.known_as || stat.player?.name || '-';
  return full;
}

export function RatingChip({
  rating,
  size = 'sm',
  derived = false,
}: {
  rating: number | null;
  size?: 'sm' | 'lg';
  /** Estimated from stats (no official rating) - shown with a small dot. */
  derived?: boolean;
}) {
  const { arabicNumerals } = useLocale();
  if (rating == null) {
    return (
      <View
        className="items-center justify-center rounded-md bg-surface-2"
        style={{ width: size === 'lg' ? 46 : 34, height: size === 'lg' ? 30 : 24 }}
      >
        <AppText variant="micro" weight="bold" className="text-muted">
          -
        </AppText>
      </View>
    );
  }
  const tone = ratingTone(rating);
  return (
    <View
      className="items-center justify-center rounded-md"
      style={{
        backgroundColor: tone.bg,
        width: size === 'lg' ? 46 : 34,
        height: size === 'lg' ? 30 : 24,
      }}
    >
      <AppText
        weight="bold"
        tabular
        style={{ color: tone.fg, fontSize: size === 'lg' ? 15 : 12 }}
      >
        {localizeDigits(rating.toFixed(1), arabicNumerals)}
      </AppText>
      {derived ? (
        <View
          className="absolute end-0.5 top-0.5 rounded-full"
          style={{ width: 3.5, height: 3.5, backgroundColor: tone.fg, opacity: 0.55 }}
        />
      ) : null}
    </View>
  );
}

/** Small circular player photo with an initials fallback. */
export function PlayerAvatar({
  uri,
  name,
  size = 30,
}: {
  uri: string | null | undefined;
  name: string;
  size?: number;
}) {
  if (!uri) {
    const initials = name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
    return (
      <View
        className="items-center justify-center rounded-full bg-surface-2"
        style={{ width: size, height: size }}
      >
        <AppText variant="micro" weight="bold" className="text-muted">
          {initials}
        </AppText>
      </View>
    );
  }
  return (
    <Image
      source={{ uri }}
      style={{ width: size, height: size, borderRadius: size / 2 }}
      contentFit="cover"
      transition={150}
      cachePolicy="memory-disk"
      accessibilityLabel={name}
    />
  );
}

/** Goal/assist/card markers for a player's match line. */
function StatMarkers({ stat }: { stat: PlayerMatchStatWithPlayer }) {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (stat.goals > 0) parts.push(`⚽×${stat.goals}`);
  if (stat.assists > 0) parts.push(`🅰×${stat.assists}`);
  if (stat.red > 0) parts.push('🟥');
  else if (stat.yellow > 0) parts.push('🟨');
  return (
    <AppText variant="micro" className="text-muted" numberOfLines={1}>
      {parts.length ? parts.join('  ') : `${stat.minutes}${t('common.min')}`}
    </AppText>
  );
}

function PlayerRow({
  stat,
  first,
  onPress,
}: {
  stat: PlayerMatchStatWithPlayer;
  first: boolean;
  onPress?: (stat: PlayerMatchStatWithPlayer) => void;
}) {
  const er = effectiveRating(stat);
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => onPress?.(stat)}
      className={`flex-row items-center gap-3 px-4 py-2.5 active:bg-surface-2/50 ${
        first ? '' : 'border-t border-line'
      }`}
    >
      <PlayerAvatar uri={stat.player?.photo_url} name={displayName(stat)} />
      <View className="flex-1">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {displayName(stat)}
        </AppText>
        <StatMarkers stat={stat} />
      </View>
      <RatingChip rating={er.value} derived={er.derived} />
    </Pressable>
  );
}

/** One team's players ranked by rating, as a glass card. */
function TeamRatings({
  team,
  stats,
  onPlayerPress,
}: {
  team: FixtureWithRefs['home_team'];
  stats: PlayerMatchStatWithPlayer[];
  onPlayerPress?: (stat: PlayerMatchStatWithPlayer) => void;
}) {
  if (stats.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-2.5 border-b border-line bg-surface-2/40 px-4 py-2.5">
        <TeamCrest uri={team?.logo_url} name={team?.name ?? '-'} size="sm" />
        <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
          {team?.name ?? '-'}
        </AppText>
      </View>
      <View>
        {stats.map((stat, index) => (
          <PlayerRow key={stat.id} stat={stat} first={index === 0} onPress={onPlayerPress} />
        ))}
      </View>
    </Card>
  );
}

/** Player-of-the-match banner: the single highest rating across both teams, with
 *  a "why" line - the key stats that earned it. */
function MotmBanner({
  stat,
  teamName,
  onPress,
}: {
  stat: PlayerMatchStatWithPlayer;
  teamName: string;
  onPress?: (stat: PlayerMatchStatWithPlayer) => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const er = effectiveRating(stat);
  const why: string[] = [];
  if (stat.goals > 0) why.push(t('match.statGoals', { count: stat.goals }));
  if (stat.assists > 0) why.push(t('match.statAssists', { count: stat.assists }));
  if (stat.saves > 0) why.push(t('match.statSaves', { count: stat.saves }));
  why.push(t('match.statMinutes', { count: stat.minutes }));
  return (
    <Card elevated className="overflow-hidden">
      <Pressable
        accessibilityRole="button"
        onPress={() => onPress?.(stat)}
        className="flex-row items-center gap-3 px-4 py-3.5 active:bg-surface-2/40"
        style={{ borderLeftWidth: 3, borderLeftColor: palette.captain }}
      >
        <PlayerAvatar uri={stat.player?.photo_url} name={displayName(stat)} size={48} />
        <View className="flex-1">
          <AppText
            variant="micro"
            weight="bold"
            className="uppercase tracking-widest"
            style={{ color: palette.captain }}
          >
            {t('match.playerOfTheMatch')}
          </AppText>
          <AppText variant="body" weight="bold" className="text-ink" numberOfLines={1}>
            {displayName(stat)}
          </AppText>
          <AppText variant="micro" className="text-soft" numberOfLines={1}>
            {teamName} · {localizeDigits(why.join(' · '), numeralOptions.arabicNumerals)}
          </AppText>
        </View>
        <RatingChip rating={er.value} size="lg" derived={er.derived} />
      </Pressable>
    </Card>
  );
}

/**
 * Per-player match ratings (API-Football). A player-of-the-match banner, then
 * each team's XI ranked by rating with goal/assist markers and a tinted chip.
 */
export function PlayerRatings({
  fixture,
  stats,
  onPlayerPress,
}: {
  fixture: FixtureWithRefs;
  stats: PlayerMatchStatWithPlayer[];
  /** Open a player's match stat sheet. */
  onPlayerPress?: (stat: PlayerMatchStatWithPlayer) => void;
}) {
  const { t } = useTranslation();

  const { homeStats, awayStats, motm, motmTeam } = useMemo(() => {
    const homeId = fixture.home_team?.id;
    const awayId = fixture.away_team?.id;
    // Rank by the EFFECTIVE rating (official when present, else derived) so every
    // player who played is rated and the best one can be player-of-the-match.
    const byRating = (a: PlayerMatchStatWithPlayer, b: PlayerMatchStatWithPlayer) =>
      (effectiveRating(b).value ?? -1) - (effectiveRating(a).value ?? -1) ||
      b.minutes - a.minutes;
    const home = stats.filter((s) => s.team_id === homeId).sort(byRating);
    const away = stats.filter((s) => s.team_id === awayId).sort(byRating);
    const rated = stats.filter((s) => effectiveRating(s).value != null).sort(byRating);
    const best = rated[0] ?? null;
    const team =
      best && best.team_id === homeId
        ? fixture.home_team?.name
        : best
          ? fixture.away_team?.name
          : null;
    return { homeStats: home, awayStats: away, motm: best, motmTeam: team ?? '' };
  }, [stats, fixture.home_team, fixture.away_team]);

  if (stats.length === 0) return null;

  return (
    <View className="gap-3 px-4">
      {motm ? <MotmBanner stat={motm} teamName={motmTeam} onPress={onPlayerPress} /> : null}
      <AppText variant="micro" weight="medium" className="px-1 text-muted">
        {t('match.ratingsHint')}
      </AppText>
      <TeamRatings team={fixture.home_team} stats={homeStats} onPlayerPress={onPlayerPress} />
      <TeamRatings team={fixture.away_team} stats={awayStats} onPlayerPress={onPlayerPress} />
    </View>
  );
}
